import { useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  usePlannedOrders,
  type PlannedOrder,
  type PlannedOrderStatus,
} from "@/api/planningRun";
import type { Flavor } from "@/api/sources";
import { DataTableFacetedFilter } from "@/components/data-table/DataTableFacetedFilter";
import { DataTablePagination } from "@/components/data-table/DataTablePagination";
import { DataTableToolbar } from "@/components/data-table/DataTableToolbar";
import {
  itemEntity,
  locationEntity,
  supplierEntity,
} from "@/components/entity-select/entities";
import { EntityFilter } from "@/components/entity-select/EntityFilter";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatDate, formatNumber } from "@/utils/formatters";

import { OrderSource } from "./OrderKind";
import { OrderStatus } from "./OrderStatus";

const DEFAULT_FILTERS = {
  item: [] as number[],
  location: [] as number[],
  supplier: [] as number[],
  flavor: [] as Flavor[],
  status: [] as PlannedOrderStatus[],
  order: [] as ("new" | "existing")[],
};

const FLAVORS = ["purchased", "manufactured", "transferred"] as const;

export function OrdersView({
  runId,
  selectedOrderId,
}: {
  runId: number;
  selectedOrderId: number | null;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState({ index: 0, size: 50 });
  const orders = usePlannedOrders({
    run: runId,
    limit: page.size,
    offset: page.index * page.size,
    item: filters.item,
    location: filters.location,
    supplier: filters.supplier,
    flavor: filters.flavor,
    status: filters.status,
    existing:
      filters.order.length === 1 ? filters.order[0] === "existing" : undefined,
  });
  const filter = (changes: Partial<typeof DEFAULT_FILTERS>) => {
    setFilters((current) => ({ ...current, ...changes }));
    setPage((current) => ({ ...current, index: 0 }));
  };

  const statusOptions = [
    {
      id: "planned" as const,
      name: t("planningReview.status.planned", "Undecided"),
    },
    {
      id: "firmed" as const,
      name: t("planningReview.status.firmed", "Accepted"),
    },
    {
      id: "cancelled" as const,
      name: t("planningReview.status.cancelled", "Discarded"),
    },
  ];
  const orderOptions = [
    { id: "new" as const, name: t("planningReview.newOrder", "New order") },
    {
      id: "existing" as const,
      name: t("planningReview.existingOrder", "Existing order"),
    },
  ];
  const flavorOptions = FLAVORS.map((id) => ({
    id,
    name: t(`sources.flavor.${id}`),
  }));
  const filtered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const count = orders.data?.count ?? 0;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-3">
        <DataTableToolbar
          filters={
            <>
              <EntityFilter
                title={t("planningReview.filters.item", "Item")}
                descriptor={itemEntity}
                value={filters.item}
                onChange={(item) => filter({ item })}
              />
              <EntityFilter
                title={t("planningReview.filters.location", "Location")}
                descriptor={locationEntity}
                value={filters.location}
                onChange={(location) => filter({ location })}
              />
              <EntityFilter
                title={t("planningReview.filters.supplier", "Supplier")}
                descriptor={supplierEntity}
                value={filters.supplier}
                onChange={(supplier) => filter({ supplier })}
              />
              <DataTableFacetedFilter
                mode="multi"
                title={t("planningReview.orderType", "Order type")}
                options={flavorOptions}
                value={flavorOptions.filter((o) =>
                  filters.flavor.includes(o.id),
                )}
                onChange={(options) =>
                  filter({ flavor: options.map((o) => o.id) })
                }
              />
              <DataTableFacetedFilter
                mode="multi"
                title={t(
                  "planningReview.acceptanceStatus",
                  "Acceptance status",
                )}
                options={statusOptions}
                value={statusOptions.filter((o) =>
                  filters.status.includes(o.id),
                )}
                onChange={(options) =>
                  filter({ status: options.map((o) => o.id) })
                }
              />
              <DataTableFacetedFilter
                mode="multi"
                title={t("planningReview.order", "Order")}
                options={orderOptions}
                value={orderOptions.filter((o) => filters.order.includes(o.id))}
                onChange={(options) =>
                  filter({ order: options.map((o) => o.id) })
                }
              />
            </>
          }
          onReset={filtered ? () => filter(DEFAULT_FILTERS) : undefined}
        />
      </div>

      <ul className="min-h-0 flex-1 divide-y overflow-auto">
        {orders.isLoading &&
          Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="p-3">
              <Skeleton className="h-20" />
            </li>
          ))}
        {orders.data?.results.length === 0 && (
          <li className="text-muted-foreground p-6 text-center text-sm">
            {t("planningReview.empty", "No orders")}
          </li>
        )}
        {orders.data?.results.map((o) => (
          <li key={o.id}>
            <OrderRow order={o} selected={o.id === selectedOrderId} />
          </li>
        ))}
      </ul>

      <div className="border-t px-3 pb-3">
        <DataTablePagination
          pageIndex={page.index}
          pageSize={page.size}
          pageCount={Math.max(1, Math.ceil(count / page.size))}
          totalCount={count}
          onPageChange={(index) =>
            setPage((current) => ({ ...current, index }))
          }
          onPageSizeChange={(size) => setPage({ index: 0, size })}
        />
      </div>
    </div>
  );
}

function OrderRow({
  order: o,
  selected,
}: {
  order: PlannedOrder;
  selected: boolean;
}) {
  const { t } = useTranslation();
  // An open order is released already; it shows its arrival instead.
  const date = o.releaseDate ?? o.dueDate;
  return (
    <Link
      to={`?order=${o.id}`}
      aria-current={selected || undefined}
      className={cn(
        "hover:bg-muted/50 aria-[current]:bg-muted grid cursor-default grid-cols-[3.5rem_minmax(0,1fr)] gap-3 p-3",
        o.status === "cancelled" && "opacity-60",
      )}
    >
      <time
        dateTime={date}
        className={cn(
          "flex flex-col self-start overflow-hidden rounded-md border text-center tabular-nums",
          o.releasePastDue && "border-destructive/50 text-destructive",
        )}
      >
        <span className="sr-only">
          {o.releaseDate
            ? t("planningReview.release", "Release")
            : t("planningReview.arrival", "Arrival")}
        </span>
        <span
          className={cn(
            "bg-muted text-xs font-medium uppercase",
            o.releasePastDue && "bg-destructive/10",
          )}
        >
          {formatDate(date, { month: "short" })}
        </span>
        <span className="text-lg leading-6 font-semibold">
          {formatDate(date, { day: "numeric" })}
        </span>
        <span className="text-muted-foreground text-xs">
          {formatDate(date, { year: "numeric" })}
        </span>
      </time>
      <div className="min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <span className="truncate text-sm font-semibold">{o.itemName}</span>
          <span
            className={cn(
              "font-semibold tabular-nums",
              o.status === "cancelled" && "line-through",
            )}
          >
            {formatNumber(Number(o.quantity), { maximumFractionDigits: 4 })}
          </span>
        </div>
        <div className="text-muted-foreground truncate text-xs">
          {o.itemSku} · {o.locationName}
        </div>
        <div className="text-muted-foreground text-xs">
          <OrderSource order={o} />
        </div>
        {o.releaseDate && (
          <div className="text-muted-foreground text-xs">
            {t("planningReview.arrival", "Arrival")} {formatDate(o.dueDate)}
          </div>
        )}
        {o.suggestedDueDate && (
          <div className="text-xs font-medium text-violet-700">
            {t(
              "planningReview.suggestedArrival",
              "Suggested arrival {{date}}",
              {
                date: formatDate(o.suggestedDueDate),
              },
            )}
          </div>
        )}
        {o.timeFence && (
          <div className="text-muted-foreground text-xs">
            {t(
              "planningReview.skipThroughDate",
              "Skip replenishment through {{date}}",
              { date: formatDate(o.timeFence) },
            )}
          </div>
        )}
        <OrderStatus status={o.status} />
      </div>
    </Link>
  );
}
